import { rewriteImageSrc } from '@/utils/rewriteImageSrc';
import classnames from 'classnames';
import { computeFont } from '../utils/fonts';
import * as styles from './Paywall.css';
import type { PaywallElementProps } from './Paywall.types';

export function PaywallElement({ element, className, theme, url = '#' }: PaywallElementProps) {
  const { data } = element.attrs || {};
  const { title, description, cta_text, benefits, subheader, image_url, style } = data || {
    title: 'Upgrade Now',
    description: 'Get access to premium content',
    cta_text: 'Subscribe',
    benefits: [],
    subheader: 'Subscription benefits',
    image_url: '',
    style: {
      show_image: true,
      background_color: '#ffffff',
      text_color: '#000',
      button_background_color: '#000000',
      button_text_color: '#ffffff',
      container_shadow: '0 0 #00000000',
    },
  };

  const hasBenefits = Array.isArray(benefits) && benefits.length > 0;
  const paragraphStyle = {
    fontFamily: `var(--wt-paragraph-font-family,${computeFont(theme?.paragraph_font_family)})`,
    fontSize: `var(--wt-paragraph-font-size,${theme?.paragraph_font_size || 0}px)`,
    lineHeight: `var(--wt-paragraph-line-height,${theme?.paragraph_line_height_size})`,
  };

  const subheaderStyle = {
    fontFamily: `var(--wt-h4-font-family,${computeFont(theme?.h4_font_family)})`,
    fontSize: `var(--wt-h4-font-size,${theme?.h4_font_size || 0}px)`,
    lineHeight: `var(--wt-h4-line-height,${theme?.h4_line_height_size})`,
    fontWeight: '600',
    marginTop: '0px',
  };

  return (
    <div
      className={classnames(className, styles.container)}
      style={{
        width: '100%',
        boxShadow: style?.container_shadow,
        backgroundColor: style?.background_color,
        color: style?.text_color,
        borderRadius: `var(--wt-button-border-radius,${theme?.button_border_radius || 0}px)`,
      }}
    >
      <div className={styles.textCenter}>
        {style?.show_image && image_url && (
          <img
            src={rewriteImageSrc(image_url)}
            alt="logo"
            className={styles.logo}
            style={{
              borderRadius: `var(--wt-button-border-radius,${theme?.button_border_radius || 0}px)`,
              overflow: 'hidden',
            }}
          />
        )}
        <h2
          className={styles.title}
          style={{
            fontFamily: `var(--wt-h2-font-family,${theme?.h2_font_family})`,
            fontSize: `var(--wt-h2-font-size,${theme?.h2_font_size || 0}px)`,
            lineHeight: `var(--wt-h2-line-height,${theme?.h2_line_height_size})`,
            marginTop: '8px',
          }}
        >
          {title}
        </h2>
        <p className={styles.description} style={paragraphStyle}>
          {description}
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.button}
          style={{
            padding: `${theme?.button_padding_top}px ${theme?.button_padding_bottom}px`,
            backgroundColor: style?.button_background_color,
            fontFamily: theme?.button_font_family,
            color: style?.button_text_color,
            borderRadius: `${theme?.button_border_radius || 0}px`,
            pointerEvents: url === '#' ? 'none' : 'auto',
            cursor: url === '#' ? 'default' : 'pointer',
          }}
          onClick={(e) => {
            if (url === '#') {
              e.preventDefault();
            }
          }}
        >
          {cta_text}
        </a>
        {hasBenefits && (
          <h4 className={styles.subheader} style={subheaderStyle}>
            {subheader}:
          </h4>
        )}
      </div>
      {hasBenefits && (
        <ul className={styles.featureList}>
          {benefits.map((benefit: string) => (
            <li key={benefit} className={styles.featureListItem} style={paragraphStyle}>
              {benefit}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
